import React, { useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import { Badge } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  customer: any;
  onToggleSidebar: () => void; // used on mobile to open drawer
  isMobile: boolean;
  basketItemCount?: number;
  onOpenBasket?: () => void;

  brandTitle?: string;
  brandLogoUrl?: string;
};

export function AccountHeader({
  customer,
  onToggleSidebar,
  isMobile,
  basketItemCount = 0,
  onOpenBasket,
  brandTitle = "Paws on Plates",
  brandLogoUrl = "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Paws_on_Plates_Tertiary_Logo_Brown.svg?v=1744270930",
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const firstName = customer?.first_name || "there";
  const initials = useMemo(() => {
    const n = `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim();
    const parts = n.split(" ").filter(Boolean);
    if (!parts.length) return "U";
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }, [customer]);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: isMobile ? "#fff" : "transparent",
        color: QUIZ.color.ink,
      }}
    >
      <Toolbar sx={{ minHeight: 72, px: { xs: 1, md: 2 }, gap: 1 }}>
        {/* Left */}
        <Box sx={{ width: 56, display: "flex", alignItems: "center" }}>
          {isMobile ? (
            <IconButton
              onClick={onToggleSidebar}
              aria-label="Open menu"
              sx={{
                width: 46,
                height: 46,
                background: QUIZ.color.blue,
                "&:hover": { background: QUIZ.color.blue },
              }}
            >
              <MenuIcon sx={{ color: QUIZ.color.ink }} />
            </IconButton>
          ) : (
            // Desktop: keep spacing consistent
            <Box />
          )}
        </Box>

        {/* Center (brand) - mobile only; desktop shows the logo in the sidebar */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {isMobile && (
            <Box
              component="img"
              src={brandLogoUrl}
              alt={brandTitle}
              sx={{
                height: 40,
                objectFit: "contain",
              }}
            />
          )}
        </Box>

        {/* Right (basket + profile) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Basket Icon */}
          {basketItemCount > 0 && onOpenBasket && (
            <Tooltip title="View basket">
              <IconButton
                onClick={onOpenBasket}
                aria-label="Open basket"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: QUIZ.color.brown,
                  "&:hover": { background: "#E96A67" },
                }}
              >
                <Badge
                  badgeContent={basketItemCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: QUIZ.color.brown,
                      color: QUIZ.color.white,
                      fontWeight: 800,
                      fontSize: 11,
                      minWidth: 20,
                      height: 20,
                    },
                  }}
                >
                  <ShoppingBasketIcon sx={{ fontSize: 22, color: QUIZ.color.white }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Profile Avatar */}
          <Tooltip title="Account">
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-label="Open account menu"
              sx={{ gap: 0.5 }}
            >
              <Avatar
                variant="rounded"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 900,
                  fontFamily: QUIZ.font.display,
                  fontSize: 18,
                  bgcolor: QUIZ.color.blue,
                  color: QUIZ.color.ink,
                }}
              >
                {initials}
              </Avatar>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{
              paper: {
                sx: { borderRadius: 3, border: "1px solid rgba(0,0,0,.10)", mt: 1 },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                Hi, {firstName}
              </Typography>
            </Box>
            <Divider />
            <MenuItem component="a" href="/account/logout" sx={{ color: "#D14C3F", fontWeight: 600 }}>
              Log out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}